import { useState } from 'react';
import { Anchor, Box, Button, CloseButton, Group, Paper, Stack, Text } from '@mantine/core';
import { useClarity } from '../hooks/useClarity';

interface CookieConsentBannerProps {
  privacyPolicyUrl?: string;
  position?: 'bottom' | 'top';
}

/**
 * GDPR-compliant cookie consent banner for Microsoft Clarity
 * Implements requirements from Section 4.4.d of Clarity Terms of Use
 */
export function CookieConsentBanner({
                                      privacyPolicyUrl = '/dpa/',
                                      position = 'bottom',
                                    }: CookieConsentBannerProps) {
  const {consent, grantAnalytics, denyConsent} = useClarity();
  const [showBanner, setShowBanner] = useState(true);

  const handleAccept = async () => {
    await grantAnalytics();
    setShowBanner(false);
  };

  const handleReject = async () => {
    await denyConsent();
    setShowBanner(false);
  };

  // Show banner only if no consent decision has been made AND showBanner is true
  if (consent !== null || !showBanner) {
    return null;
  }

  return (
    <Box
      pos="fixed"
      left={0}
      right={0}
      p="md"
      style={{
        [position]: 0,
        zIndex: 9999,
      }}
    >
      <Box maw={1200} mx="auto">
        <Paper withBorder p="lg" radius="md" shadow="md">
          <Group justify="space-between" mb="xs">
            <Text fz="md" fw={500}>
              Cookie Consent
            </Text>
            <CloseButton
              mr={-9}
              mt={-9}
              onClick={handleReject}
              aria-label="close-cookie-consent-banner"
            />
          </Group>

          <Stack gap="md">
            <Text c="dimmed" fz="xs">
              We use Microsoft Clarity, a third-party analytics service, to analyze user
              behavior and improve website performance. Microsoft may collect personal data
              from your visit to provide this service. By clicking "Accept", you consent to
              the use of analytics cookies.{' '}
              <Anchor
                href="https://www.microsoft.com/en-us/privacy/privacystatement"
                target="_blank"
                rel="noopener noreferrer"
                fz="xs"
                aria-label="microsoft-privacy-statement-link"
              >
                Microsoft Privacy Statement
              </Anchor>
              {' | '}
              <Anchor
                href={privacyPolicyUrl}
                fz="xs"
                aria-label="our-dpa-link"
              >
                Data Processing Addendum
              </Anchor>
            </Text>

            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                size="xs"
                onClick={handleReject}
                aria-label="reject-cookies"
              >
                Reject
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={handleAccept}
                aria-label="accept-cookies"
              >
                Accept
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}

